import {
  Button as ChakraButton,
  Flex,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { Modal, showToast } from '@diamond/shared/ui';
import { useEffect, useMemo, useState } from 'react';

import { useUpdateLHCItemRemarkMutation } from '../../hooks';
import type { LHCDetailInvoiceRowData } from '../../types';

interface RemarkLHCModalProps {
  isOpen: boolean;
  onClose: () => void;
  lhcId: string;
  rowData: LHCDetailInvoiceRowData | null;
  onSaved?: () => void;
}

export function RemarkLHCModal({
  isOpen,
  onClose,
  lhcId,
  rowData,
  onSaved,
}: RemarkLHCModalProps) {
  const toast = useToast();
  const [remark, setRemark] = useState('');

  const initialRemark = useMemo(() => {
    if (!rowData || rowData.remark === '-') {
      return '';
    }
    return rowData.remark || '';
  }, [rowData]);

  useEffect(() => {
    if (isOpen) {
      setRemark(initialRemark);
    }
  }, [initialRemark, isOpen]);

  const updateRemarkMutation = useUpdateLHCItemRemarkMutation();

  const isFormValid = remark.trim() !== '';
  const isDirty = remark.trim() !== initialRemark.trim();

  const handleSubmit = () => {
    if (!rowData || !lhcId) return;

    updateRemarkMutation.mutate(
      {
        lhcId,
        itemId: rowData.rowId,
        payload: {
          remark: remark.trim(),
        },
      },
      {
        onSuccess: () => {
          showToast(toast, 'success', 'Keterangan berhasil disimpan');
          onSaved?.();
          onClose();
        },
        onError: (error: Error) => {
          showToast(toast, 'error', error.message || 'Gagal menyimpan keterangan');
        },
      }
    );
  };

  const handleClose = () => {
    setRemark(initialRemark);
    onClose();
  };

  return (
    <Modal
      title="Keterangan LHC"
      name="remark-lhc-modal"
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      isCentered
      hideAction
      scrollBehavior="outside"
      modalBodyProps={{ pb: 6 }}
      modalHeaderProps={{ fontSize: '24px', fontWeight: 500, pb: 4 }}
      modalContentProps={{ w: '544px', maxW: '544px' }}
    >
      <FormControl>
        <FormLabel
          fontSize="14px"
          fontWeight={400}
          color="gray.600"
          mb={2}
          display="none"
        >
          Keterangan
        </FormLabel>
        <Textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Tulis keterangan LHC disini"
          w="480px"
          minH="88px"
          borderRadius="8px"
          borderColor="gray.75"
          bg="white"
          _hover={{ borderColor: 'gray.75' }}
          _focus={{ borderColor: 'blue', boxShadow: '0 0 0 1px #0000A3' }}
          resize="vertical"
          p={4}
          fontSize="sm"
        />
      </FormControl>

      <Flex justify="flex-end" mt={6}>
        <ChakraButton
          bgColor={isFormValid && isDirty ? '#0000A3' : '#E8E8ED'}
          color={isFormValid && isDirty ? 'white' : '#8E8E93'}
          _hover={{
            bg: isFormValid && isDirty ? '#00008A' : '#E8E8ED',
          }}
          isDisabled={!isFormValid || !isDirty}
          isLoading={updateRemarkMutation.isPending}
          onClick={handleSubmit}
          px={6}
          borderRadius="md"
        >
          Simpan
        </ChakraButton>
      </Flex>
    </Modal>
  );
}
